import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const NotaryCard = ({ notary, onBookAppointment }) => {
  const formatAvailabilityHours = (availableHours) => {
    if (!availableHours || typeof availableHours !== 'object') return 'Non spécifié';

    const days = Object.keys(availableHours);
    if (days?.length === 0) return 'Non spécifié';

    const firstDay = days?.[0];
    return availableHours?.[firstDay] || 'Non spécifié';
  };

  const getSpecializationBadgeColor = (specialization) => {
    const colors = {
      'Real Estate': 'bg-blue-100 text-blue-800',
      'Property Transfer': 'bg-green-100 text-green-800',
      'Inheritance': 'bg-purple-100 text-purple-800',
      'Business Law': 'bg-orange-100 text-orange-800',
      'Family Law': 'bg-pink-100 text-pink-800',
      'Contract Law': 'bg-indigo-100 text-indigo-800',
      'Droit immobilier': 'bg-blue-100 text-blue-800',
      'Succession': 'bg-purple-100 text-purple-800',
      'Contrat': 'bg-indigo-100 text-indigo-800'
    };
    return colors?.[specialization] || 'bg-gray-100 text-gray-800';
  };

  // Get the display name with priority: notary_name > user_profile.full_name
  const getNotaryDisplayName = () => {
    if (notary?.notary_name?.trim()) {
      return notary?.notary_name?.trim();
    }
    if (notary?.user_profile?.full_name?.trim()) {
      return notary?.user_profile?.full_name?.trim();
    }
    return 'Notaire non disponible';
  };

  return (
    <div className="bg-card border-border rounded-2xl hover:shadow-warm transition-all duration-300 p-6 group border-none border-0">
      {/* Header with Enhanced Notary Name Display */}
      <div className="flex items-start space-x-4 mb-4">
        <div className="w-16 h-16 bg-gradient-to-br from-brand-gold to-brand-earth rounded-xl flex items-center justify-center group-hover:scale-105 transition-transform duration-300">
          {notary?.user_profile?.avatar_url ?
          <img
            src={notary?.user_profile?.avatar_url}
            alt={getNotaryDisplayName()}
            className="w-full h-full rounded-xl object-cover" /> :


          <Icon name="UserCheck" size={24} color="white" />
          }
        </div>
        
        <div className="flex-1">
          <div className="flex items-center space-x-2 mb-1">
            <h3 className="text-ios-title2 font-display font-display-bold text-text-primary group-hover:text-brand-earth transition-colors duration-300">
              {getNotaryDisplayName()}
            </h3>
            {notary?.is_verified &&
            <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center animate-pulse">
                <Icon name="Check" size={14} color="white" />
              </div>
            }
            {!notary?.is_verified &&
            <div className="px-2 py-1 bg-yellow-100 text-yellow-800 text-ios-caption2 rounded-md">
                En attente
              </div>
            }
          </div>
          <p className="text-ios-callout text-text-secondary font-system font-body-medium">
            Licence: <span className="font-body-semibold text-brand-earth">{notary?.license_number}</span>
          </p>
          <p className="text-ios-caption1 text-brand-gold font-system font-body-medium">
            ID: {notary?.id?.slice(0, 8)}...
          </p>
        </div>
      </div>

      {/* Enhanced Contact Info */}
      <div className="space-y-2 mb-4">
        {notary?.user_profile?.email &&
        <div className="flex items-center space-x-3 text-ios-callout text-text-secondary group-hover:text-text-primary transition-colors duration-300">
            <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center">
              <Icon name="Mail" size={16} className="text-blue-600" />
            </div>
            <span className="font-system font-body-normal">{notary?.user_profile?.email}</span>
          </div>
        }
        {notary?.user_profile?.phone &&
        <div className="flex items-center space-x-3 text-ios-callout text-text-secondary group-hover:text-text-primary transition-colors duration-300">
            <div className="w-8 h-8 bg-green-50 rounded-lg flex items-center justify-center">
              <Icon name="Phone" size={16} className="text-green-600" />
            </div>
            <span className="font-system font-body-normal">{notary?.user_profile?.phone}</span>
          </div>
        }
        <div className="flex items-start space-x-3 text-ios-callout text-text-secondary group-hover:text-text-primary transition-colors duration-300">
          <div className="w-8 h-8 bg-red-50 rounded-lg flex items-center justify-center mt-0.5">
            <Icon name="MapPin" size={16} className="text-red-600" />
          </div>
          <span className="font-system font-body-normal flex-1">{notary?.office_address}</span>
        </div>
      </div>

      {/* Enhanced Specializations */}
      {notary?.specializations && notary?.specializations?.length > 0 &&
      <div className="mb-4">
          <p className="text-ios-caption1 text-text-secondary mb-2 font-system font-body-semibold uppercase tracking-wide">
            Spécialisations
          </p>
          <div className="flex flex-wrap gap-2">
            {notary?.specializations?.slice(0, 3)?.map((spec, index) =>
          <span
            key={index}
            className={`px-2 py-1 rounded-lg text-ios-caption1 font-system font-body-semibold shadow-sm ${getSpecializationBadgeColor(spec)} hover:shadow-md transition-shadow duration-300`}>

                {spec}
              </span>
          )}
            {notary?.specializations?.length > 3 &&
          <span className="px-2 py-1 rounded-lg text-ios-caption1 font-system font-body-semibold bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 hover:shadow-md transition-shadow duration-300">
                +{notary?.specializations?.length - 3} autres
              </span>
          }
          </div>
        </div>
      }

      {/* Enhanced Availability */}
      <div className="mb-6">
        <p className="text-ios-caption1 text-text-secondary mb-2 font-system font-body-semibold uppercase tracking-wide">
          Disponibilités
        </p>
        <div className="flex items-center space-x-3 text-ios-callout text-text-primary bg-green-50 p-3 rounded-xl">
          <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
            <Icon name="Clock" size={16} className="text-green-600" />
          </div>
          <span className="font-system font-body-medium">{formatAvailabilityHours(notary?.available_hours)}</span>
        </div>
      </div>

      {/* Enhanced Actions */}
      <div className="flex gap-3">
        <Button
          variant="outline"
          size="sm"
          iconName="MessageCircle"
          className="flex-1 hover:bg-blue-50 hover:border-blue-300 transition-colors duration-300">

          Contacter
        </Button>
        <Button
          variant="default"
          size="sm"
          iconName="Calendar"
          className="flex-1 bg-gradient-to-r from-brand-gold to-brand-earth hover:from-brand-earth hover:to-brand-gold transition-all duration-300 transform hover:scale-105"
          onClick={() => onBookAppointment?.(notary)}>

          Prendre RDV
        </Button>
      </div>

      {/* Creation date indicator for newly added notaries */}
      {notary?.created_at &&
      <div className="mt-4 pt-4 border-t border-border">
          <p className="text-ios-caption2 text-text-secondary text-center font-system font-body-normal">
            Ajouté le {new Date(notary?.created_at)?.toLocaleDateString('fr-FR', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
          })}
          </p>
        </div>
      }
    </div>);

};

export default NotaryCard;